import { motion } from "framer-motion";
import {
    UserCheck,
    DollarSign,
    Landmark,
    KanbanSquare,
    FileText,
    Sparkles,
    MessageSquare,
    CheckSquare,
    GitBranch,
    Goal,
    Timer,
    Clock4,
    Calendar,
    Bell,
    CalendarClock,
    TrendingUp,
    ScrollText,
    Workflow,
    ClipboardList,
    Palmtree,
    FileInput,
    Brain,
    PenTool,
    Search,
    Eye,
    Receipt,
    Wallet,
    ShoppingCart,
    BookOpen,
    Users2,
    UserPlus,
    Building2,
    Briefcase,
    LineChart,
    Layers,
    Target,
    Flag,
    ListChecks,
    Filter,
    LayoutGrid,
    PieChart,
    BarChart3,
    HardDrive,
    Lock,
    Shield,
    KeyRound,
    History,
    UserCog,
    Smartphone,
    Globe2,
    LifeBuoy,
    Package,
    StickyNote,
    FolderTree,
    BookMarked,
    Repeat2,
    Boxes,
    Calculator,
    Crown,
    GanttChartSquare,
    Network,
    Pencil,
    Files,
} from "lucide-react";

/* ─── All features ──────────────────────────────────────── */
const features = [
    // Work
    { icon: KanbanSquare, label: "Projects" },
    { icon: CheckSquare, label: "Tasks" },
    { icon: Layers, label: "Subtasks" },
    { icon: LayoutGrid, label: "Kanban" },
    { icon: GanttChartSquare, label: "Gantt" },
    { icon: GitBranch, label: "Roadmaps" },
    { icon: Repeat2, label: "Sprints" },
    { icon: Flag, label: "Milestones" },
    { icon: Goal, label: "Goals" },
    { icon: Network, label: "Dependencies" },
    { icon: Target, label: "Priorities" },
    { icon: Files, label: "Files" },
    { icon: ListChecks, label: "Checklists" },
    { icon: Filter, label: "Saved Views" },

    // Time
    { icon: Timer, label: "Time Tracking" },
    { icon: Clock4, label: "Timesheets" },
    { icon: Calendar, label: "Calendar" },
    { icon: CalendarClock, label: "Scheduling" },
    { icon: Bell, label: "Reminders" },

    // Docs & Knowledge
    { icon: FileText, label: "Docs" },
    { icon: StickyNote, label: "Notes" },
    { icon: BookOpen, label: "Wikis" },
    { icon: Brain, label: "Mind Maps" },
    { icon: PenTool, label: "Whiteboards" },

    // Communication
    { icon: MessageSquare, label: "Chat" },

    // AI
    { icon: Sparkles, label: "AI Co‑pilot" },
    { icon: Pencil, label: "AI Writer" },
    { icon: Search, label: "Search" },
    { icon: Eye, label: "AI Insights" },

    // Automation
    { icon: FileInput, label: "Forms" },
    { icon: DollarSign, label: "Revenue Tracking" },
    { icon: Landmark, label: "Balance Sheets" },
    { icon: Shield, label: "Role-Based Access" },
    { icon: Smartphone, label: "Mobile Access" },
    { icon: HardDrive, label: "Cloud Storage" },

    // CRM & Sales
    { icon: Building2, label: "CRM" },
    { icon: Users2, label: "Contacts" },
    { icon: UserPlus, label: "Leads" },
    { icon: Briefcase, label: "Deals" },
    { icon: LineChart, label: "Forecasts" },
    { icon: TrendingUp, label: "Sales Intelligence" },
    { icon: ScrollText, label: "Quotations" },
    { icon: Workflow, label: "Pipelines" },

    // Finance
    { icon: Receipt, label: "Invoicing" },
    { icon: Wallet, label: "Expenses" },
    { icon: ShoppingCart, label: "Purchases" },
    { icon: Calculator, label: "Books" },

    // HR & People
    { icon: UserCog, label: "HR" },
    { icon: Crown, label: "Payroll" },
    { icon: FolderTree, label: "Org Chart" },
    { icon: Users2, label: "Guests" },
    { icon: Clock4, label: "Shifts" },
    { icon: ClipboardList, label: "Attendance" },
    { icon: Palmtree, label: "Leave Management" },

    // Analytics
    { icon: BarChart3, label: "Reporting" },
    { icon: PieChart, label: "Dashboards" },
    { icon: UserCheck, label: "Onboarding" },

    // Platform & Security
    { icon: KeyRound, label: "SSO / SAML" },
    { icon: Shield, label: "RBAC" },
    { icon: History, label: "Audit Logs" },
    { icon: Lock, label: "Encryption" },
    { icon: HardDrive, label: "Backups" },
    { icon: Globe2, label: "Data Residency" },
];

const Cell = ({ icon: Icon, label, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.4, delay: Math.min(index * 0.012, 0.4) }}
        whileHover={{ y: -2 }}
        className="group relative aspect-[5/4] flex flex-col items-center justify-center gap-2.5 p-4 bg-obsidian-200 hover:bg-obsidian-100 transition-colors duration-300"
        data-testid={`feature-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
        <Icon
            className="w-5 h-5 text-zinc-500 group-hover:text-electric transition-colors"
            strokeWidth={1.5}
        />
        <span className="text-[11px] sm:text-xs font-medium text-zinc-400 group-hover:text-white text-center leading-tight transition-colors">
            {label}
        </span>
    </motion.div>
);

export const FeatureMatrix = () => {
    return (
        <section
            id="all-features"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="feature-matrix-section"
        >
            <div className="absolute inset-0 radial-glow-amber opacity-40 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                    <div className="max-w-2xl">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                            $ ls --all
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                            {features.length}+ capabilities.
                            <br />
                            <span className="text-zinc-500">
                                One workspace. Zero integrations.
                            </span>
                        </h2>
                    </div>
                    <p className="md:max-w-sm text-zinc-400 font-light text-sm">
                        Every primitive your team needs — built on the same
                        schema, queryable from the same API, automatable from
                        the same SDK.
                    </p>
                </div>

                {/* Uniform feature grid */}
                <div
                    className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]"
                    data-testid="feature-matrix-grid"
                >
                    {features.map((f, i) => (
                        <Cell
                            key={`${f.label}-${i}`}
                            icon={f.icon}
                            label={f.label}
                            index={i}
                        />
                    ))}
                </div>

                {/* Footnote */}
                {/* <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-500 font-code">
                    <span>
                        <span className="text-zinc-600">{"//"}</span> iOS ·
                        Android · macOS · Windows · Linux · Web
                    </span>
                    <span className="text-zinc-600">
                        SOC 2 · ISO 27001 · GDPR · HIPAA*
                    </span>
                </div> */}
            </div>
        </section>
    );
};
